import { Component } from '@theme/component';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const HOLD_DURATION = 0.35;
const TRANSITION_DURATION = 0.65;
const FADE_DURATION = 0.2;
const OUTGOING_STAGGER_SPAN = 0.5;
const INCOMING_START = 0.3;
const INCOMING_STAGGER_SPAN = 0.5;

function smoothstep(edge0, edge1, value) {
  const progress = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return progress * progress * (3 - 2 * progress);
}

export function getOutgoingLineOpacity(index, lineCount, progress) {
  const start = (index / Math.max(lineCount - 1, 1)) * OUTGOING_STAGGER_SPAN;
  return 1 - smoothstep(start, start + FADE_DURATION, progress);
}

export function getIncomingLineOpacity(index, lineCount, progress) {
  const start = INCOMING_START + (index / Math.max(lineCount - 1, 1)) * INCOMING_STAGGER_SPAN;
  return smoothstep(start, start + FADE_DURATION, progress);
}

export function buildStoryTimeline(stateCount) {
  const count = Math.max(0, Math.floor(stateCount));
  const timeline = [];

  for (let state = 0; state < count; state += 1) {
    timeline.push({ type: 'hold', state, duration: HOLD_DURATION });
    if (state < count - 1) {
      timeline.push({ type: 'transition', from: state, to: state + 1, duration: TRANSITION_DURATION });
    }
  }

  return timeline;
}

export function resolveStoryTimeline(timeline, progress) {
  if (timeline.length === 0) return { type: 'empty', localProgress: 0 };

  const totalDuration = timeline.reduce((total, segment) => total + segment.duration, 0);
  const time = Math.min(1, Math.max(0, progress)) * totalDuration;
  let elapsed = 0;

  for (const segment of timeline) {
    const end = elapsed + segment.duration;
    if (time <= end) {
      const localProgress = segment.duration === 0 ? 1 : (time - elapsed) / segment.duration;
      const { duration: _duration, ...resolved } = segment;
      return { ...resolved, localProgress: Math.min(1, Math.max(0, localProgress)) };
    }
    elapsed = end;
  }

  const { duration: _duration, ...last } = timeline[timeline.length - 1];
  return { ...last, localProgress: 1 };
}

export function getStorySectionHeight(stageHeight, timeline, multiplier) {
  const duration = timeline.reduce((total, segment) => total + segment.duration, 0);
  const safeMultiplier = Number.isFinite(multiplier) ? Math.max(0, multiplier) : 1;
  return stageHeight * (1 + duration * safeMultiplier);
}

export function getStoryProgress(sectionTop, sectionHeight, stageHeight, stickyOffset) {
  const available = sectionHeight - stageHeight;
  if (available <= 0) return 0;
  return Math.min(1, Math.max(0, (stickyOffset - sectionTop) / available));
}

function splitVisualLines(source) {
  const copy = source.cloneNode(true);
  copy.removeAttribute('data-story-text');
  copy.setAttribute('data-story-lines', '');
  copy.setAttribute('aria-hidden', 'true');
  copy.classList.add('nervape-scroll-story__text-lines');
  source.classList.add('nervape-scroll-story__text-source');
  source.insertAdjacentElement('afterend', copy);

  const visualLines = [];
  const walker = document.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    let offset = 0;
    for (const character of textNode.textContent ?? '') {
      const nextOffset = offset + character.length;
      const range = document.createRange();
      range.setStart(textNode, offset);
      range.setEnd(textNode, nextOffset);
      const rect = range.getBoundingClientRect();
      offset = nextOffset;
      if (rect.width === 0 && rect.height === 0) continue;

      const top = Math.round(rect.top);
      const line = visualLines.find((entry) => Math.abs(entry.top - top) <= 1);
      if (line) line.characters.push(character);
      else visualLines.push({ top, characters: [character] });
    }
    textNode = walker.nextNode();
  }

  copy.replaceChildren(
    ...visualLines.map(({ characters }) => {
      const line = document.createElement('span');
      line.className = 'nervape-scroll-story__line';
      line.dataset.storyLine = '';
      line.textContent = characters.join('').replace(/\s+/g, ' ').trim();
      return line;
    })
  );
}

class NervapeScrollStory extends Component {
  #abortController = new AbortController();
  #resizeObserver;
  #scrollTarget;
  #layoutTimer;
  #raf;
  #timeline = [];
  #states = [];
  #stage;
  #stickyOffset = 0;
  #editorState = null;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#configure();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#configure();
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  #configure() {
    this.#teardown();
    this.#abortController = new AbortController();
    this.#states = [...this.querySelectorAll('[data-story-state]')];
    this.#stage = this.querySelector('[data-story-sticky]');

    this.#motionQuery.addEventListener('change', () => this.#configure(), { signal: this.#abortController.signal });
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, {
        signal: this.#abortController.signal,
      });
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, {
        signal: this.#abortController.signal,
      });
    }

    if (this.dataset.storyMode !== 'scroll' || this.#states.length < 2 || this.#motionQuery.matches || !this.#stage) {
      return;
    }

    this.#timeline = buildStoryTimeline(this.#states.length);
    this.classList.add('is-scroll-enhanced');
    this.#bindScrollTarget();

    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollRootChange, {
      signal: this.#abortController.signal,
    });
    window.addEventListener('pageshow', this.#scheduleLayout, { signal: this.#abortController.signal });
    window.addEventListener('resize', this.#scheduleLayout, { signal: this.#abortController.signal });

    this.#resizeObserver = new ResizeObserver(this.#scheduleLayout);
    this.#resizeObserver.observe(this.#stage);
    const header = document.querySelector('#header-component[sticky]');
    if (header) this.#resizeObserver.observe(header);
    this.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', this.#scheduleLayout, { signal: this.#abortController.signal });
      image.addEventListener('error', this.#scheduleLayout, { signal: this.#abortController.signal });
    });

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#scheduleLayout();
    });
    this.#layout();
  }

  #teardown() {
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#scrollTarget?.removeEventListener('scroll', this.#scheduleRender);
    this.#scrollTarget = undefined;
    clearTimeout(this.#layoutTimer);
    cancelAnimationFrame(this.#raf);
    this.#layoutTimer = undefined;
    this.#raf = undefined;
    this.#editorState = null;
    this.classList.remove('is-scroll-enhanced');
    this.style.removeProperty('--story-section-height');
    this.style.removeProperty('--story-sticky-offset');
    this.style.removeProperty('--story-heading-height');
    this.style.removeProperty('--story-details-height');
    delete this.dataset.storyProgress;
    delete this.dataset.storySegment;
    this.#states.forEach((state) => {
      delete state.dataset.storyVisible;
      state.style.removeProperty('opacity');
      state.querySelector('[data-story-background]')?.style.removeProperty('clip-path');
      state.querySelector('[data-story-foreground]')?.style.removeProperty('clip-path');
      state.querySelectorAll('[data-story-lines]').forEach((copy) => copy.remove());
      state.querySelectorAll('[data-story-text]').forEach((source) =>
        source.classList.remove('nervape-scroll-story__text-source')
      );
    });
  }

  #bindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#scheduleRender);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#scheduleRender, { passive: true });
  }

  #handleScrollRootChange = () => {
    this.#bindScrollTarget();
    this.#scheduleLayout();
  };

  #scheduleLayout = () => {
    clearTimeout(this.#layoutTimer);
    this.#layoutTimer = window.setTimeout(() => this.#layout(), 100);
  };

  #layout() {
    if (!this.classList.contains('is-scroll-enhanced') || !this.#stage) return;

    const header = document.querySelector('#header-component[sticky]');
    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    this.#stickyOffset = header ? headerHeight : 0;
    this.style.setProperty('--story-sticky-offset', `${this.#stickyOffset}px`);

    const stageHeight = this.#stage.getBoundingClientRect().height;
    const multiplier = Number.parseFloat(getComputedStyle(this).getPropertyValue('--story-scroll-distance-multiplier')) || 1;
    this.style.setProperty('--story-section-height', `${getStorySectionHeight(stageHeight, this.#timeline, multiplier)}px`);
    this.style.removeProperty('--story-heading-height');
    this.style.removeProperty('--story-details-height');
    this.#rebuildLines();
    if (window.matchMedia('(max-width: 1279px)').matches) {
      // Shared text rows keep the image wipe aligned when titles and copy wrap differently.
      const headingHeight = Math.max(...this.#states.map((state) =>
        state.querySelector('.nervape-scroll-story__heading[data-story-lines]')?.getBoundingClientRect().height ?? 0
      ));
      const detailsHeight = Math.max(...this.#states.map((state) =>
        state.querySelector('.nervape-scroll-story__details')?.getBoundingClientRect().height ?? 0
      ));
      this.style.setProperty('--story-heading-height', `${headingHeight}px`);
      this.style.setProperty('--story-details-height', `${detailsHeight}px`);
    }
    this.#render();
  }

  #rebuildLines() {
    this.#states.forEach((state) => {
      const visibility = state.dataset.storyVisible;
      state.dataset.storyVisible = 'true';
      state.querySelectorAll('[data-story-lines]').forEach((copy) => copy.remove());
      state.querySelectorAll('[data-story-text]').forEach((source) => {
        source.classList.remove('nervape-scroll-story__text-source');
        splitVisualLines(source);
      });
      if (visibility === undefined) delete state.dataset.storyVisible;
      else state.dataset.storyVisible = visibility;
    });
  }

  #scheduleRender = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = undefined;
      this.#render();
    });
  };

  #render() {
    if (!this.#stage || this.#editorState !== null) return;
    const rect = this.getBoundingClientRect();
    const progress = getStoryProgress(rect.top, this.offsetHeight, this.#stage.offsetHeight, this.#stickyOffset);
    this.dataset.storyProgress = progress.toFixed(4);
    this.#renderSegment(resolveStoryTimeline(this.#timeline, progress));
  }

  #renderSegment(segment) {
    this.#states.forEach((state) => {
      state.dataset.storyVisible = 'false';
      state.style.opacity = '1';
      state.querySelectorAll('[data-story-line]').forEach((line) => line.style.setProperty('opacity', '1'));
      state.querySelector('[data-story-background]')?.style.setProperty('clip-path', 'inset(0 0 0 0)');
      state.querySelector('[data-story-foreground]')?.style.setProperty('clip-path', 'inset(0 0 0 0)');
    });

    if (segment.type === 'hold') {
      this.dataset.storySegment = `hold:${segment.state}`;
      this.#showState(segment.state);
      return;
    }

    if (segment.type === 'transition') {
      this.dataset.storySegment = `transition:${segment.from}-${segment.to}`;
      const outgoing = this.#showState(segment.from);
      const incoming = this.#showState(segment.to);
      if (!outgoing || !incoming) return;

      this.#setLineOpacities(outgoing, (index, count) =>
        getOutgoingLineOpacity(index, count, segment.localProgress)
      );
      this.#setLineOpacities(incoming, (index, count) =>
        getIncomingLineOpacity(index, count, segment.localProgress)
      );
      const inset = `${(1 - segment.localProgress) * 100}% 0 0 0`;
      incoming.querySelector('[data-story-background]')?.style.setProperty('clip-path', `inset(${inset})`);
      incoming.querySelector('[data-story-foreground]')?.style.setProperty('clip-path', `inset(${inset})`);
    }
  }

  #setLineOpacities(state, getOpacity) {
    const lines = [...state.querySelectorAll('[data-story-line]')];
    lines.forEach((line, index) => line.style.setProperty('opacity', String(getOpacity(index, lines.length))));
  }

  #showState(index) {
    const state = this.#states[index];
    if (!state) return null;
    state.dataset.storyVisible = 'true';
    return state;
  }

  #getEventState(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-story-state]') : null;
    if (target) return this.contains(target) ? target : null;
    const { blockId, sectionId } = event.detail ?? {};
    if (!blockId || !sectionId || this.id !== `NervapeScrollStory-${sectionId}`) return null;
    return this.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
  }

  #handleBlockSelect = (event) => {
    const state = this.#getEventState(event);
    const index = this.#states.indexOf(state);
    if (index < 0) return;
    this.#editorState = index;
    this.#renderSegment({ type: 'hold', state: index, localProgress: 1 });
  };

  #handleBlockDeselect = (event) => {
    if (!this.#getEventState(event)) return;
    this.#editorState = null;
    this.#render();
  };
}

if (!customElements.get('nervape-scroll-story')) {
  customElements.define('nervape-scroll-story', NervapeScrollStory);
}
